import { components } from '../.generated';
import { OneService } from '../OneService';

type SubscriptionEntry = components['schemas']['SubscriptionEntry'];
type ResourceGroupEntry = components['schemas']['ResourceGroupEntry'];
type ResourceEntry = components['schemas']['ResourceEntry'];

/** Page size for list queries. The Graph list queries are always paginated. */
const PAGE_LIMIT = 100;
/** Hard stop so a backend that never shrinks a page can't loop forever. */
const MAX_PAGES = 100;

/**
 * Resources provider — subscriptions, resource groups and resources over the
 * Graph CQRS layer (replacing the hand-written `/subscriptions...` REST calls).
 * Each `list*` transparently pages through the whole result set and returns the
 * generated `*Entry` types, so callers get one flat, fully-typed array.
 */
export class ResourcesProvider {
  constructor(private readonly one: OneService) {}

  async listSubscriptions(): Promise<SubscriptionEntry[]> {
    return this.pageAll(async (skip, limit) => {
      const query: components['schemas']['ListSubscriptionsQuery'] = {
        type: 'ListSubscriptionsQuery',
        skip,
        limit,
      };
      const res = await this.one.query<
        components['schemas']['ListSubscriptionsQueryResult']
      >({ query });
      return res.entries ?? [];
    });
  }

  async listResourceGroups(
    subscriptionId: string
  ): Promise<ResourceGroupEntry[]> {
    return this.pageAll(async (skip, limit) => {
      const query: components['schemas']['ListResourceGroupsQuery'] = {
        type: 'ListResourceGroupsQuery',
        subscriptionId,
        skip,
        limit,
      };
      const res = await this.one.query<
        components['schemas']['ListResourceGroupsQueryResult']
      >({ query });
      return res.entries ?? [];
    });
  }

  async listResources(
    subscriptionId: string,
    resourceGroup: string
  ): Promise<ResourceEntry[]> {
    return this.pageAll(async (skip, limit) => {
      const query: components['schemas']['ListResourcesQuery'] = {
        type: 'ListResourcesQuery',
        subscriptionId,
        resourceGroup,
        skip,
        limit,
      };
      const res = await this.one.query<
        components['schemas']['ListResourcesQueryResult']
      >({ query });
      return res.entries ?? [];
    });
  }

  /** Accumulate every page (skip/limit) into a single array. */
  private async pageAll<T>(
    fetchPage: (skip: number, limit: number) => Promise<T[]>
  ): Promise<T[]> {
    const all: T[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
      const items = await fetchPage(page * PAGE_LIMIT, PAGE_LIMIT);
      all.push(...items);
      if (items.length < PAGE_LIMIT) break;
    }
    return all;
  }
}
